'use client'

import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  AlertCircle,
  Bug,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Clock,
  CreditCard,
  HelpCircle,
  Image as ImageIcon,
  Lightbulb,
  Lock,
  LogIn,
  Paperclip,
  Send,
  Sparkles,
  Upload,
  UserPlus,
  X,
} from 'lucide-react'
import {
  SUPPORT_CATEGORY_LABELS,
  type SupportTicketCategory,
} from '@repo/ui/support'

interface Props {
  initialName: string | null
  initialEmail: string | null
  userId: string | null
}

interface UploadedAttachment {
  path: string
  name: string
  type: string
  size: number
}

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

const SEVERITY_OPTIONS: Array<{
  value: Severity
  label: string
  hint: string
  dot: string
  border: string
  text: string
  bg: string
}> = [
  { value: 'LOW', label: 'Low', hint: 'Minor or cosmetic', dot: 'bg-emerald-400', border: 'border-emerald-300', text: 'text-emerald-900', bg: 'bg-emerald-50/70' },
  { value: 'MEDIUM', label: 'Medium', hint: 'Annoying — I have a workaround', dot: 'bg-amber-400', border: 'border-amber-300', text: 'text-amber-900', bg: 'bg-amber-50/70' },
  { value: 'HIGH', label: 'High', hint: 'Hard to keep using the app', dot: 'bg-orange-500', border: 'border-orange-300', text: 'text-orange-900', bg: 'bg-orange-50/70' },
  { value: 'URGENT', label: 'Urgent', hint: "I'm fully blocked", dot: 'bg-red-500', border: 'border-red-300', text: 'text-red-900', bg: 'bg-red-50/70' },
]

const CATEGORY_META: Record<
  SupportTicketCategory,
  { Icon: React.ComponentType<{ className?: string }>; tint: string }
> = {
  ACCOUNT: { Icon: Lock, tint: 'text-indigo-700 bg-indigo-50' },
  BILLING: { Icon: CreditCard, tint: 'text-emerald-700 bg-emerald-50' },
  BOOKING: { Icon: CalendarClock, tint: 'text-blue-700 bg-blue-50' },
  TECHNICAL: { Icon: Bug, tint: 'text-rose-700 bg-rose-50' },
  QUESTION: { Icon: HelpCircle, tint: 'text-cyan-700 bg-cyan-50' },
  FEEDBACK: { Icon: Lightbulb, tint: 'text-amber-700 bg-amber-50' },
  OTHER: { Icon: Sparkles, tint: 'text-gray-700 bg-gray-50' },
}

const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024
const ATTACHMENT_MAX_COUNT = 5

/** Categories that we can only act on with the submitter's records in hand.
 *  Billing + booking issues require us to look up sessions/charges tied to a
 *  specific account; anon submissions there go nowhere. ACCOUNT and TECHNICAL
 *  stay anon-allowed because the most common anon use cases there are exactly
 *  "I can't log in" / "the site is broken" — both of which need the anon path
 *  to work. */
const SIGNIN_REQUIRED_CATEGORIES = new Set<SupportTicketCategory>([
  'BILLING',
  'BOOKING',
])

function gatherBrowserContext() {
  if (typeof window === 'undefined') return {}
  return {
    url: window.location.href,
    referrer: document.referrer || null,
    userAgent: navigator.userAgent,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
}

export function HelpFormClient({ initialName, initialEmail, userId }: Props) {
  const isAuthed = !!userId

  const [category, setCategory] = useState<SupportTicketCategory | null>(null)
  const [customCategory, setCustomCategory] = useState('')
  const [severity, setSeverity] = useState<Severity | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [whenStarted, setWhenStarted] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [submitterEmail, setSubmitterEmail] = useState(initialEmail ?? '')
  const [submitterName, setSubmitterName] = useState(initialName ?? '')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [isDraggingFile, setIsDraggingFile] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successInfo, setSuccessInfo] = useState<{
    ticketNumber: number
    accessToken: string
  } | null>(null)

  async function handleFiles(files: FileList | null) {
    setAttachmentError(null)
    if (!files || files.length === 0) return
    if (attachments.length + files.length > ATTACHMENT_MAX_COUNT) {
      setAttachmentError(`You can attach up to ${ATTACHMENT_MAX_COUNT} files.`)
      return
    }
    setUploading(true)
    try {
      const next: UploadedAttachment[] = []
      for (const file of Array.from(files)) {
        if (file.size > ATTACHMENT_MAX_BYTES) {
          setAttachmentError(`${file.name} is over 25 MB — too large to attach.`)
          continue
        }
        const fd = new FormData()
        fd.append('file', file)
        const res = await fetch('/api/support/attachment', { method: 'POST', body: fd })
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string }
          setAttachmentError(`Couldn't upload ${file.name}: ${data.error ?? res.statusText}`)
          continue
        }
        next.push((await res.json()) as UploadedAttachment)
      }
      setAttachments((prev) => [...prev, ...next])
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function removeAttachment(att: UploadedAttachment) {
    const res = await fetch('/api/support/attachment', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: att.path }),
    })
    if (res.ok) {
      setAttachments((prev) => prev.filter((a) => a.path !== att.path))
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)

    if (!category) {
      setError('Please pick a category so we can route this to the right team.')
      return
    }
    if (!isAuthed && SIGNIN_REQUIRED_CATEGORIES.has(category)) {
      setError('Please sign in to submit this kind of request.')
      return
    }
    if (!subject.trim() || !body.trim()) {
      setError('Subject and description are required.')
      return
    }
    if (category === 'OTHER' && !customCategory.trim()) {
      setError('Please describe the category.')
      return
    }
    if (!severity) {
      setError('Please tell us how urgent this is.')
      return
    }
    if (!isAuthed && !submitterEmail.trim()) {
      setError('Please provide an email so we can reach you.')
      return
    }
    if (!isAuthed && !submitterName.trim()) {
      setError('Please tell us your first name.')
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        sourceApp: 'CLIENT',
        category,
        customCategory: category === 'OTHER' ? customCategory.trim() : null,
        priority: severity ?? 'MEDIUM',
        subject: subject.trim(),
        body: body.trim(),
        userId,
        submitterEmail: !isAuthed ? submitterEmail.trim() : null,
        submitterName: !isAuthed ? submitterName.trim() || null : null,
        attachments: attachments.map((a) => ({
          path: a.path,
          name: a.name,
          type: a.type,
        })),
        context: {
          ...gatherBrowserContext(),
          whenStarted: whenStarted || null,
          severity,
        },
      }

      const res = await fetch('/api/support/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        if (res.status === 429) {
          throw new Error("You've sent too many tickets recently. Please wait a bit and try again.")
        }
        const data = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(data.error ?? 'Something went wrong. Please try again.')
      }
      const data = (await res.json()) as {
        id: string
        ticketNumber: number
        accessToken: string
      }
      setSuccessInfo({ ticketNumber: data.ticketNumber, accessToken: data.accessToken })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }

  if (successInfo) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <h3 className="mt-3 text-lg font-semibold text-emerald-900">
          Ticket #{successInfo.ticketNumber} received
        </h3>
        <p className="mt-2 text-sm text-emerald-800">
          Thanks for reaching out. We'll reply by email
          {isAuthed ? '' : ' at the address you provided'} within 24–48 hours during business hours.
        </p>
        {!isAuthed && (
          <p className="mt-3 text-xs text-emerald-700">
            We'll also email you a link to view this ticket and respond.
          </p>
        )}
      </div>
    )
  }

  const detailsComplete = subject.trim().length > 0 && body.trim().length > 0
  const categoryComplete =
    category !== null && (category !== 'OTHER' || customCategory.trim().length > 0)
  const severityComplete = severity !== null
  const contactComplete =
    isAuthed || (submitterEmail.trim().length > 0 && submitterName.trim().length > 0)
  const needsSignin =
    !isAuthed && category !== null && SIGNIN_REQUIRED_CATEGORIES.has(category)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
        <div className="min-w-0 flex-1 leading-snug">
          <div className="font-medium text-slate-900">
            We address every request within 24–48 hours.
          </div>
          <div className="text-xs text-slate-600">
            Monday–Friday business hours. Tickets opened on weekends and holidays are picked
            up the next business day.
          </div>
        </div>
      </div>

      {!isAuthed && (
        <SectionCard step={1} title="How do we reach you?" complete={contactComplete}>
          <Field label="Email" required>
            <input
              type="email"
              value={submitterEmail}
              onChange={(e) => setSubmitterEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
            />
          </Field>
          <Field label="Your first name" required>
            <input
              type="text"
              value={submitterName}
              onChange={(e) => setSubmitterName(e.target.value)}
              placeholder="First name"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
            />
          </Field>
        </SectionCard>
      )}

      <SectionCard
        step={isAuthed ? 1 : 2}
        title="What's this about?"
        subtitle="Helps us route the ticket to the right team."
        complete={categoryComplete}
      >
        <Field label="Category" required>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(Object.entries(SUPPORT_CATEGORY_LABELS) as Array<[SupportTicketCategory, string]>).map(
              ([value, label]) => {
                const meta = CATEGORY_META[value]
                const Icon = meta.Icon
                const active = category === value
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setCategory(value)}
                    className={`group flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition ${
                      active
                        ? 'border-slate-700 bg-slate-50 shadow-sm ring-2 ring-slate-200'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${meta.tint}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className={`text-sm font-medium ${active ? 'text-slate-900' : 'text-gray-900'}`}>
                      {label}
                    </span>
                  </button>
                )
              }
            )}
          </div>
        </Field>

        {category === 'OTHER' && (
          <Field label="Tell us a bit more" required>
            <input
              type="text"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              placeholder="e.g. Account merge, partner billing question…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
            />
          </Field>
        )}

        {needsSignin && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <LogIn className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-amber-900">
                  This one needs you signed in
                </h4>
                <p className="mt-1 text-xs leading-relaxed text-amber-900/80">
                  We need your account to help with{' '}
                  <span className="font-medium">
                    {category && SUPPORT_CATEGORY_LABELS[category]?.toLowerCase()}
                  </span>{' '}
                  issues. Sign in (or create an account) so we can find you in our
                  system — otherwise we can't act on this kind of request.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href="/login?returnTo=%2Fdashboard%2Fsupport"
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-slate-800"
                  >
                    <LogIn className="h-3.5 w-3.5" />
                    Sign in
                  </a>
                  <a
                    href="/signup?returnTo=%2Fdashboard%2Fsupport"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-50"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Create an account
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

      </SectionCard>

      <SectionCard
        step={isAuthed ? 2 : 3}
        title="How urgent is it?"
        subtitle="So we know how quickly to jump on this."
        complete={severityComplete}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SEVERITY_OPTIONS.map((opt) => {
            const active = severity === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSeverity(opt.value)}
                className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition ${
                  active
                    ? `${opt.border} ${opt.bg} ring-2 ring-offset-1 ring-slate-200`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                  <span className={`text-sm font-medium ${active ? opt.text : 'text-gray-900'}`}>
                    {opt.label}
                  </span>
                </div>
                <span className="text-[11px] text-gray-500">{opt.hint}</span>
              </button>
            )
          })}
        </div>
      </SectionCard>

      <SectionCard
        step={isAuthed ? 3 : 4}
        title="Tell us what's happening"
        subtitle="A short subject + a clear description."
        complete={detailsComplete}
      >
        <Field label="Subject" required>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Can't sign in, billing question, looking for a therapist"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
            maxLength={120}
          />
        </Field>

        <Field label="Describe what happened" required>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What were you trying to do? What did you see? Anything we should know?"
            rows={5}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
          />
        </Field>

        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition ${showAdvanced ? 'rotate-180' : ''}`} />
            {showAdvanced ? 'Hide' : 'Add'} a little more context
          </button>
          {showAdvanced && (
            <div className="mt-3">
              <Field label="When did this start?">
                <input
                  type="text"
                  value={whenStarted}
                  onChange={(e) => setWhenStarted(e.target.value)}
                  placeholder="e.g. This morning, last Tuesday, after the iOS update"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
                />
              </Field>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        step={isAuthed ? 4 : 5}
        title="Attachments (optional)"
        subtitle="Screenshots make troubleshooting much faster."
        complete
      >
        <div
          onDragEnter={(e) => {
            e.preventDefault()
            setIsDraggingFile(true)
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setIsDraggingFile(false)}
          onDrop={(e) => {
            e.preventDefault()
            setIsDraggingFile(false)
            handleFiles(e.dataTransfer.files)
          }}
          className={`rounded-xl border-2 border-dashed p-5 text-center transition ${
            isDraggingFile
              ? 'border-slate-700 bg-slate-50'
              : 'border-gray-300 bg-gray-50/50 hover:border-gray-400'
          }`}
        >
          <Upload className="mx-auto mb-2 h-6 w-6 text-gray-400" />
          <div className="text-sm font-medium text-gray-900">Drag screenshots or files here</div>
          <div className="mt-1 text-xs text-gray-500">
            or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="font-medium text-slate-700 hover:text-slate-900"
            >
              browse
            </button>
            . Up to 5 files, 25 MB each.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {attachmentError && (
          <div className="mt-2 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{attachmentError}</span>
          </div>
        )}

        {(uploading || attachments.length > 0) && (
          <ul className="mt-3 space-y-2">
            {uploading && (
              <li className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-600">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
                Uploading…
              </li>
            )}
            {attachments.map((a) => {
              const isImage = a.type.startsWith('image/')
              return (
                <li
                  key={a.path}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 text-gray-600">
                    {isImage ? <ImageIcon className="h-3.5 w-3.5" /> : <Paperclip className="h-3.5 w-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-gray-900">{a.name}</div>
                    <div className="text-[10px] text-gray-500">
                      {Math.ceil(a.size / 1024)} KB · {a.type}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a)}
                    className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    aria-label={`Remove ${a.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </SectionCard>

      <div className="flex items-center justify-end gap-2">
        <button
          type="submit"
          disabled={
            submitting ||
            !detailsComplete ||
            !categoryComplete ||
            !severityComplete ||
            !contactComplete ||
            needsSignin
          }
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {submitting ? (
            <>
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Sending…
            </>
          ) : (
            <>
              <Send className="h-3.5 w-3.5" />
              Submit ticket
            </>
          )}
        </button>
      </div>
    </form>
  )
}

function SectionCard({
  step,
  title,
  subtitle,
  complete,
  children,
}: {
  step: number
  title: string
  subtitle?: string
  complete?: boolean
  children: ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            complete ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-700'
          }`}
        >
          {complete ? <CheckCircle2 className="h-4 w-4" /> : step}
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-4 p-5">{children}</div>
    </section>
  )
}

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
